import { FC } from 'react';
import { t } from 'i18next';
import { Box, Button, TextField } from '@mui/material';
import { CustomModal, FileUploader, SaveChangesModal } from '@components';

import { Select } from 'ui';
import { MAXIMUM_STRING_LENGTH } from '@common/types/constants.ts';
import { usePatientFileUploadModal } from './hooks';

interface UploadDocumentsModalProps {
  patient_id: string;
  isOpen: boolean;
  handleClose: () => void;
  task_id?: string;
}

export const PatientFileUploadModal: FC<UploadDocumentsModalProps> = ({
  isOpen,
  handleClose,
  patient_id,
  task_id,
}) => {
  const {
    categoryOptions,
    handleChangeCategory,
    selectedCategory,
    handleChangeFile,
    handleUploadDocument,
    isUploadDisabled,
    file,
    handleCloseAction,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    isValid,
    isFileUploading,
    handleChangeSpecification,
    specification,
  } = usePatientFileUploadModal({ patient_id, task_id, handleClose });

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleCloseAction}
        title={t('patient.documents.uploadModal.title')}
      >
        <Select
          sx={{ pb: 2 }}
          name='documents-category'
          size='medium'
          label={t('patient.documents.uploadModal.category')}
          value={selectedCategory}
          onChange={handleChangeCategory}
          options={categoryOptions}
        />

        {selectedCategory === 'other' && (
          <TextField
            value={specification}
            type='text'
            size='medium'
            onChange={handleChangeSpecification}
            label={t('patient.documents.uploadModal.specification')}
            variant='outlined'
            fullWidth
            sx={{ pb: 2 }}
            slotProps={{
              htmlInput: {
                maxLength: MAXIMUM_STRING_LENGTH,
              },
            }}
          />
        )}

        <FileUploader
          acceptFormat='documents'
          file={file}
          setFile={handleChangeFile}
        />

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, py: 3 }}
        >
          <Button variant='outlined' onClick={handleCloseAction}>
            {t('patient.documents.uploadModal.cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={handleUploadDocument}
            sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
            color='secondary'
            disabled={isUploadDisabled}
          >
            {t('patient.documents.uploadModal.upload')}
          </Button>
        </Box>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleUploadDocument}
        discardChanges={handleDiscardChanges}
        isLoading={isFileUploading}
      />
    </>
  );
};
